import React from 'react';
import { Menu, DeleteIcon, DeleteIconContainer } from './style';
import ColorDropdown from 'routes/surface/components/link-context-menu/components/color-dropdown';
import LinkStartShapeDropdown from 'routes/surface/components/link-context-menu/components/link-start-shape-dropdown';
import LinkEndShapeDropdown from 'routes/surface/components/link-context-menu/components/link-end-shape-dropdown';
import LinkLineShapeDropdown from 'routes/surface/components/link-context-menu/components/link-line-shape-dropdown';
import CopyAndLinkButton from 'routes/surface/components/link-context-menu/components/copy-reference-and-link-button';
import LinkNameInput from 'routes/surface/components/link-context-menu/components/link-name-input';
import useMutation from 'hooks/use-mutation';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import { useTranslation } from 'i18n';
import { useParams } from 'react-router';
import { useNodeLinking } from 'state/canvas/node-linking';
import { useSelectedLinkOperations } from 'state/canvas/selected-link';
import { updateSurfaceNodeLinkSettingsMutation } from 'graphql/surface-node/mutations/update-surface-node-link-settings';
import { color } from 'styles/settings';
import { set } from 'lodash/fp';
import { LineShape, LinkType, SurfaceNodePointsTo } from 'graphql/types';
import { LinkSettings, Position } from 'types';
import { toast } from 'services/toast';

export interface LinkContextMenuProps {
  style?: any;
  position?: Position;
  scale?: number;
  onDelete?: Function;
  link: SurfaceNodePointsTo;
  onChange?: (settings: LinkSettings) => void;
}

function LinkContextMenu({ onDelete, link, ...props }: LinkContextMenuProps) {
  const { t } = useTranslation();
  const { id } = useParams();

  const {
    canLinkSurfaceNode,
    canUnLinkSurfaceNode,
  } = useCurrentSurfacePermissions();

  const { clearSelectedLink } = useSelectedLinkOperations();

  const { findShapeById } = useCanvas();
  const { findReverseLink } = useFindNodeInCache();

  const [_, { updateLinkSettingsInCache }] = useNodeLinking(id as string);

  const [startShape, setStartShape] = React.useState<LineShape>(
    link?.settings?.startShape ?? LineShape.Line,
  );
  const [endShape, setEndShape] = React.useState<LineShape>(
    link?.settings?.endShape ?? LineShape.Line,
  );
  const [linkType, setLinkType] = React.useState<LinkType>(
    link?.settings?.linkType ?? LinkType.Full,
  );
  const [selectedColor, setSelectedColor] = React.useState<string>(
    link?.settings?.color ?? color.brand.greenDark,
  );
  const [linkName, setLinkName] = React.useState<string>(
    link?.settings?.linkName ?? '',
  );

  const [__, { removeConnection, addConnection, deleteLink }] = useNodeLinking(
    id as string,
  );

  const [updateSurfaceNodeLinkSettings] = useMutation(
    updateSurfaceNodeLinkSettingsMutation,
  );

  const isVisible =
    link.settings?.isVisible &&
    (canLinkSurfaceNode() || canUnLinkSurfaceNode(link));

  const isEditable = link.settings?.isEditable;

  React.useEffect(() => {
    const settings = {
      startShape,
      endShape,
      linkName,
      linkType,
      color: selectedColor,
    };

    props.onChange && props.onChange(settings);
  }, [startShape, endShape, linkType, selectedColor, linkName]);

  const isGlobalLink =
    link?.from && link?.to && link?.from?.surface?.id !== link?.to?.surface?.id;

  function updateLinkSettings(
    link: SurfaceNodePointsTo,
    settings: LinkSettings,
  ) {
    if (link.from && link.to && link.settings) {
      updateLinkSettingsInCache({ link, settings });

      updateSurfaceNodeLinkSettings({
        variables: {
          input: {
            fromId: link.from.id,
            toId: link.to.id,
            settings,
          },
        },
      });
    }
  }

  function onColorChange(color) {
    setSelectedColor(color);

    updateLinkSettings(link, { color });
  }

  function onStartShapeChange(startShape) {
    const fromShape = findShapeById(link.from.id);
    const toShape = findShapeById(link.to.id);

    const isStartNodeOnTheRight = fromShape?.x() > toShape?.x();

    if (isStartNodeOnTheRight) {
      setEndShape(startShape);
      const reverseLink = findReverseLink(link);
      if (reverseLink) {
        updateLinkSettings(reverseLink, { startShape });
      }
    } else {
      setStartShape(startShape);
      updateLinkSettings(link, { startShape });
    }
  }

  function onEndShapeChange(endShape) {
    const fromShape = findShapeById(link.from.id);
    const toShape = findShapeById(link.to.id);

    const isEndNodeOnTheLeft = toShape?.x() < fromShape?.x();
    if (isEndNodeOnTheLeft) {
      setStartShape(endShape);
      const reverseLink = findReverseLink(link);
      if (reverseLink) {
        updateLinkSettings(reverseLink, { endShape });
      }
    } else {
      setEndShape(endShape);
      updateLinkSettings(link, { endShape });
    }
  }

  function onLinkTypeChange(linkType) {
    setLinkType(linkType);
    updateLinkSettings(link, { linkType });
  }

  function onLinkNameBlur(event) {
    const linkName = event?.target?.value;
    setLinkName(linkName);
    updateLinkSettings(link, { linkName });
  }

  function unLinkNodes() {
    if (link) {
      onDelete && onDelete();

      removeConnection(link);

      clearSelectedLink();

      return deleteLink(link).then((response) => {
        if (response.isSuccess) {
          return toast.success(t('Successfully deleted a link.'));
        }

        addConnection(link);
      });
    }
  }

  if (isVisible && isEditable) {
    return (
      <Menu scale={props.scale} position={props.position}>
        <LinkNameInput
          name="linkName"
          value={linkName}
          placeholder={t('Link name')}
          onBlur={onLinkNameBlur}
        />
        <ColorDropdown
          selected={selectedColor}
          options={Object.values(color.linkColors)}
          onChange={onColorChange}
        />
        <LinkStartShapeDropdown
          selected={startShape}
          color={selectedColor}
          onChange={onStartShapeChange}
        />
        <LinkLineShapeDropdown
          selected={linkType}
          color={selectedColor}
          onChange={onLinkTypeChange}
        />
        <LinkEndShapeDropdown
          selected={endShape}
          color={selectedColor}
          onChange={onEndShapeChange}
        />

        {isGlobalLink && <CopyAndLinkButton globalLink={link} />}

        {canUnLinkSurfaceNode(link) && (
          <DeleteIconContainer>
            <DeleteIcon onClick={unLinkNodes} />
          </DeleteIconContainer>
        )}
      </Menu>
    );
  }
  return null;
}

export default LinkContextMenu;
